import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/* ─── types ───────────────────────────────────────────────── */
interface Tenant {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone_number: string | null;
  unit_number: string | null;
  property_name: string | null;
  property_id: number | null;
  meter_serial: string | null;
  meter_id: number | null;
  meter_status: "online" | "offline" | "fault" | null;
  valve_open: boolean;
  balance_units: number;
  total_paid: number;
  last_payment: string | null;
}

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (n: number, d = 0) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-2xl ${className}`} />;
}

function initials(t: Tenant) {
  const f = t.first_name?.[0] ?? "";
  const l = t.last_name?.[0]  ?? "";
  return (f + l).toUpperCase() || t.username[0].toUpperCase();
}

const AVATAR_COLORS = [
  "from-sky-400 to-blue-600",
  "from-violet-400 to-purple-600",
  "from-teal-400 to-emerald-600",
  "from-rose-400 to-pink-600",
  "from-amber-400 to-orange-500",
  "from-indigo-400 to-blue-700",
];
function avatarColor(id: number) { return AVATAR_COLORS[id % AVATAR_COLORS.length]; }

const METER_META = {
  online:  { dot: "bg-emerald-400", label: "Online",  text: "text-emerald-600", bg: "bg-emerald-50" },
  offline: { dot: "bg-slate-400",   label: "Offline", text: "text-slate-500",   bg: "bg-slate-100"  },
  fault:   { dot: "bg-rose-400",    label: "Fault",   text: "text-rose-600",    bg: "bg-rose-50"    },
};

function balanceColor(b: number) {
  if (b === 0)  return { text: "text-rose-600",   bg: "bg-rose-50",   bar: "bg-rose-400",   label: "Empty",    icon: "⛔" };
  if (b <= 10)  return { text: "text-amber-600",  bg: "bg-amber-50",  bar: "bg-amber-400",  label: "Low",      icon: "⚠️" };
  if (b <= 30)  return { text: "text-sky-600",    bg: "bg-sky-50",    bar: "bg-sky-400",    label: "Moderate", icon: "💧" };
  return              { text: "text-emerald-600", bg: "bg-emerald-50", bar: "bg-emerald-400", label: "Good",   icon: "✅" };
}

/* ─── tenant drawer ───────────────────────────────────────── */
function TenantDrawer({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const bc = balanceColor(tenant.balance_units);
  const meterMeta = tenant.meter_status ? METER_META[tenant.meter_status] : null;
  const balancePct = Math.min(100, (tenant.balance_units / 100) * 100);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[420px] bg-white h-full shadow-2xl overflow-y-auto flex flex-col">

        {/* header */}
        <div className="bg-gradient-to-br from-[#0f2557] via-[#1e3a8a] to-[#1d4ed8] px-6 py-7 flex-shrink-0">
          <div className="flex items-center justify-between mb-5">
            <span className="text-sky-300/60 text-[11.5px] font-bold uppercase tracking-widest">Tenant Profile</span>
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors p-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${avatarColor(tenant.id)} flex items-center justify-center text-[20px] font-black text-white shadow-xl flex-shrink-0`}>
              {initials(tenant)}
            </div>
            <div>
              <p className="text-white font-black text-[18px] leading-tight">{tenant.first_name} {tenant.last_name}</p>
              <p className="text-sky-300/70 text-[12.5px] mt-0.5">@{tenant.username}</p>
              {tenant.property_name && (
                <p className="text-sky-300/50 text-[12px] mt-0.5">
                  {tenant.property_name}
                  {tenant.unit_number && <> · Unit <span className="font-semibold">{tenant.unit_number}</span></>}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 p-5 space-y-4">

          {/* contact */}
          <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Contact Information</p>
            {[
              { icon: "📧", label: "Email", value: tenant.email },
              { icon: "📱", label: "Phone", value: tenant.phone_number ?? "Not provided" },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-3">
                <span className="text-base">{c.icon}</span>
                <div>
                  <p className="text-[11px] text-gray-400 font-medium">{c.label}</p>
                  <p className="text-[13px] font-semibold text-gray-800">{c.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* balance card */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Water Balance</p>
              <span className={`text-[11.5px] font-bold px-2.5 py-1 rounded-lg ${bc.bg} ${bc.text}`}>
                {bc.icon} {bc.label}
              </span>
            </div>
            <p className="text-[32px] font-black text-gray-800 leading-none">
              {fmt(tenant.balance_units, 1)}
              <span className="text-[15px] font-semibold text-gray-400 ml-1.5">units</span>
            </p>
            <div className="mt-3 w-full bg-gray-100 rounded-full h-2.5">
              <div className={`h-2.5 rounded-full ${bc.bar} transition-all`} style={{ width: `${balancePct}%` }} />
            </div>
          </div>

          {/* meter */}
          {tenant.meter_serial ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Meter</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[15px] font-bold text-gray-800 font-mono">{tenant.meter_serial}</p>
                  {meterMeta && (
                    <span className={`inline-flex items-center gap-1.5 mt-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${meterMeta.bg} ${meterMeta.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meterMeta.dot}`}/>
                      {meterMeta.label}
                    </span>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[12.5px] font-bold px-3 py-2 rounded-xl ${tenant.valve_open ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                  {tenant.valve_open ? "💧 Open" : "🔴 Closed"}
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-5 text-center">
              <p className="text-[13px] text-gray-400 font-medium">No meter assigned to this unit yet</p>
            </div>
          )}

          {/* payment stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-2xl p-4">
              <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-bold">Total Paid</p>
              <p className="text-[17px] font-black text-emerald-700 mt-1 leading-tight">KES {fmt(tenant.total_paid)}</p>
            </div>
            <div className="bg-sky-50 rounded-2xl p-4">
              <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-bold">Last Payment</p>
              <p className="text-[14px] font-black text-sky-700 mt-1 leading-tight">
                {tenant.last_payment
                  ? new Date(tenant.last_payment).toLocaleDateString("en-KE", { dateStyle: "medium" })
                  : "No payments"}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─── tenant row ─────────────────────────────────────────── */
function TenantRow({ tenant, onClick }: { tenant: Tenant; onClick: () => void }) {
  const bc       = balanceColor(tenant.balance_units);
  const meterMeta = tenant.meter_status ? METER_META[tenant.meter_status] : null;

  return (
    <tr
      onClick={onClick}
      className="hover:bg-sky-50/50 cursor-pointer transition-colors border-b border-gray-50 last:border-0 group"
    >
      {/* name */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${avatarColor(tenant.id)} flex items-center justify-center text-[12px] font-black text-white flex-shrink-0 shadow-sm`}>
            {initials(tenant)}
          </div>
          <div>
            <p className="text-[13px] font-bold text-gray-800">{tenant.first_name} {tenant.last_name}</p>
            <p className="text-[11.5px] text-gray-400">@{tenant.username}</p>
          </div>
        </div>
      </td>

      {/* property & unit */}
      <td className="px-5 py-4">
        <p className="text-[13px] font-semibold text-gray-700">{tenant.property_name ?? "—"}</p>
        <p className="text-[11.5px] text-gray-400">{tenant.unit_number ? `Unit ${tenant.unit_number}` : "—"}</p>
      </td>

      {/* meter */}
      <td className="px-5 py-4">
        {tenant.meter_serial ? (
          <div>
            <p className="text-[13px] font-mono font-semibold text-gray-700">{tenant.meter_serial}</p>
            {meterMeta && (
              <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-1.5 py-0.5 rounded-md mt-0.5 ${meterMeta.bg} ${meterMeta.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meterMeta.dot}`} />
                {meterMeta.label}
              </span>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-gray-300 font-medium italic">No meter</span>
        )}
      </td>

      {/* balance */}
      <td className="px-5 py-4">
        <span className={`inline-block text-[13px] font-black px-2.5 py-1 rounded-lg ${bc.bg} ${bc.text}`}>
          {fmt(tenant.balance_units, 1)} units
        </span>
      </td>

      {/* valve */}
      <td className="px-5 py-4">
        <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 py-1 rounded-full ${tenant.valve_open ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tenant.valve_open ? "bg-emerald-400 animate-pulse" : "bg-slate-300"}`} />
          {tenant.valve_open ? "Open" : "Closed"}
        </span>
      </td>

      {/* payments */}
      <td className="px-5 py-4">
        <p className="text-[13px] font-bold text-gray-800">KES {fmt(tenant.total_paid)}</p>
        <p className="text-[11.5px] text-gray-400">
          {tenant.last_payment
            ? new Date(tenant.last_payment).toLocaleDateString("en-KE", { dateStyle: "medium" })
            : "No payments"}
        </p>
      </td>

      {/* chevron */}
      <td className="px-4 py-4">
        <svg viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={2} className="w-4 h-4 ml-auto group-hover:stroke-sky-400 transition-colors">
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function TenantsPage() {
  const [tenants, setTenants]       = useState<Tenant[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [propFilter, setPropFilter] = useState("all");
  const [balFilter, setBalFilter]   = useState<"all" | "good" | "low" | "empty">("all");
  const [selected, setSelected]     = useState<Tenant | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounts/tenants/")
      .then(({ data }) => setTenants(data.results ?? data))
      .catch(() => setTenants([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const properties = ["all", ...Array.from(new Set(tenants.map((t) => t.property_name).filter(Boolean))) as string[]];

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) ||
      t.username.toLowerCase().includes(q) ||
      t.email.toLowerCase().includes(q) ||
      (t.phone_number ?? "").includes(q) ||
      (t.unit_number ?? "").toLowerCase().includes(q) ||
      (t.meter_serial ?? "").toLowerCase().includes(q);
    const matchProp = propFilter === "all" || t.property_name === propFilter;
    const matchBal  =
      balFilter === "all"   ? true :
      balFilter === "empty" ? t.balance_units === 0 :
      balFilter === "low"   ? t.balance_units > 0 && t.balance_units <= 10 :
      balFilter === "good"  ? t.balance_units > 10 : true;
    return matchSearch && matchProp && matchBal;
  });

  const total        = tenants.length;
  const lowBalance   = tenants.filter((t) => t.balance_units > 0 && t.balance_units <= 10).length;
  const emptyBalance = tenants.filter((t) => t.balance_units === 0).length;
  const valveOpen    = tenants.filter((t) => t.valve_open).length;
  const totalRevenue = tenants.reduce((s, t) => s + Number(t.total_paid), 0);

  const BAL_TABS = [
    { key: "all" as const,   label: "All",       count: total,        dot: "" },
    { key: "good" as const,  label: "Healthy",   count: tenants.filter((t) => t.balance_units > 10).length, dot: "bg-emerald-400" },
    { key: "low" as const,   label: "Low",       count: lowBalance,   dot: "bg-amber-400" },
    { key: "empty" as const, label: "Empty",     count: emptyBalance, dot: "bg-rose-400" },
  ];

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Tenants</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">
              {loading ? "Loading…" : `${total} tenant${total !== 1 ? "s" : ""} across all properties`}
            </p>
          </div>
          {!loading && emptyBalance > 0 && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-600 text-[12.5px] font-bold px-4 py-2.5 rounded-xl">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 flex-shrink-0">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              {emptyBalance} tenant{emptyBalance > 1 ? "s" : ""} out of water
            </div>
          )}
        </div>

        {/* ── KPI strip ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: "Total Tenants",  value: total.toString(),          sub: "Registered",        color: "bg-sky-50",     stroke: "#0ea5e9" },
              { label: "Healthy",        value: tenants.filter((t) => t.balance_units > 10).length.toString(), sub: "> 10 units",    color: "bg-emerald-50", stroke: "#10b981" },
              { label: "Low Balance",    value: lowBalance.toString(),     sub: "1–10 units left",   color: "bg-amber-50",   stroke: "#f59e0b" },
              { label: "Empty Balance",  value: emptyBalance.toString(),   sub: "Water cut off",     color: "bg-rose-50",    stroke: "#ef4444" },
              { label: "Valves Open",    value: valveOpen.toString(),      sub: "Currently flowing", color: "bg-teal-50",    stroke: "#14b8a6" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-all">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-bold truncate">{s.label}</p>
                  <p className="text-[20px] font-black text-gray-800 leading-tight">{s.value}</p>
                  <p className="text-[11px] text-gray-400 truncate">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── alert banner ── */}
        {!loading && (lowBalance + emptyBalance) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={2} className="w-5 h-5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <p className="text-[13px] font-bold text-amber-800">
                {lowBalance + emptyBalance} tenant{lowBalance + emptyBalance > 1 ? "s" : ""} with critically low or empty balance
              </p>
              <p className="text-[12.5px] text-amber-700/80 mt-0.5">
                Consider sending top-up SMS reminders. Tenants with empty balances have had their valves automatically closed.
              </p>
            </div>
          </div>
        )}

        {/* ── total revenue callout ── */}
        {!loading && totalRevenue > 0 && (
          <div className="bg-gradient-to-r from-[#0f2557] to-[#1e3a8a] rounded-2xl px-6 py-5 flex items-center justify-between">
            <div>
              <p className="text-sky-300/70 text-[12px] font-bold uppercase tracking-widest">All-time Revenue Collected</p>
              <p className="text-white text-[28px] font-black mt-0.5">KES {fmt(totalRevenue)}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#7dd3fc" strokeWidth={1.8} className="w-7 h-7">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <path d="M2 10h20M6 15h4" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
        )}

        {/* ── filters ── */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          {/* search */}
          <div className="relative flex-1 min-w-[220px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone, unit, meter…"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white shadow-sm"
            />
          </div>

          {/* property filter */}
          <select
            value={propFilter}
            onChange={(e) => setPropFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] font-medium text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
          >
            {properties.map((p) => (
              <option key={p} value={p}>{p === "all" ? "All Properties" : p}</option>
            ))}
          </select>

          {/* balance tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {BAL_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setBalFilter(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${balFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                {t.dot && <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />}
                {t.label}
                <span className={`text-[10.5px] px-1.5 py-0.5 rounded-md font-bold ${balFilter === t.key ? "bg-gray-100 text-gray-600" : "bg-gray-200/80 text-gray-400"}`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── table ── */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.4} className="w-8 h-8">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="font-bold text-gray-700 text-[15px]">
              {search || propFilter !== "all" || balFilter !== "all" ? "No tenants match your filter" : "No tenants yet"}
            </p>
            <p className="text-[13px] text-gray-400 mt-1.5">
              {search || propFilter !== "all" || balFilter !== "all"
                ? "Try adjusting your search or filter."
                : "Tenants appear here once they register and are assigned to a unit."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    {["Tenant", "Property & Unit", "Meter", "Balance", "Valve", "Payments", ""].map((h) => (
                      <th key={h} className="px-5 py-4 text-left text-[11px] text-gray-400 font-bold uppercase tracking-wider bg-gray-50/80 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <TenantRow key={t.id} tenant={t} onClick={() => setSelected(t)} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/60 flex items-center justify-between">
              <span className="text-[12px] text-gray-400">{filtered.length} tenant{filtered.length !== 1 ? "s" : ""}</span>
              <span className="text-[12px] text-gray-400">Click any row to view full profile →</span>
            </div>
          </div>
        )}

      </div>

      {selected && <TenantDrawer tenant={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
